import React, { useCallback } from 'react'
import { Popover, BtnV2, l, Icon as ManyUIIcon } from '@manychat/manyui'

import { IButton } from 'common/builder/buttons/buttonInterfaces'
import ButtonForm from 'common/builder/components/builder/ButtonForm'
import { ButtonStatsViewer } from 'common/builder/components/ButtonStatsViewer/ButtonStatsViewer'
import { ButtonType } from 'common/builder/constants/ButtonType'
import { newOnboardingService } from 'utils/services/newOnboardingService'

interface PopoverContentProps {
  builderId: string
  isFallback: boolean
  onDeselect: () => void
  anchor: HTMLElement | null
  readonly: boolean
  button: IButton
  selected: boolean
  errors: Array<{ prop: string }>
  showErrors: boolean
  buttonIsNotDraggable: boolean
  dirty: boolean
  onRemove: () => void
  hideSaveButton: boolean
}

export const PopoverContent = ({
  builderId,
  isFallback,
  onDeselect,
  anchor,
  readonly,
  button,
  selected,
  errors,
  showErrors,
  buttonIsNotDraggable,
  dirty,
  onRemove,
  hideSaveButton,
}: PopoverContentProps) => {
  const onRequestClose = useCallback(
    (keyValue: string) => {
      if (keyValue === 'esc' && newOnboardingService.isAnyOnboardingRunning()) {
        return
      }

      onDeselect()
    },
    [onDeselect],
  )

  const isPopoverShown = () => {
    if (!readonly) {
      return selected && anchor != null
    }
    return (
      selected && anchor != null && button.type !== ButtonType.SHARE && !button.stats?.ctrDisabled
    )
  }

  return (
    <Popover
      key="popover"
      at="right"
      arrow
      open={isPopoverShown()}
      anchor={anchor}
      onRequestClose={onRequestClose}
      formWithPages
      fullscreenOnMobile
    >
      {!readonly ? (
        <>
          <Popover.Header>
            {isFallback ? l.translate('Edit Fallback') : l.translate('Edit Button')}
          </Popover.Header>
          <Popover.Body>
            <ButtonForm
              className="p-x-md p-b-md"
              builderId={builderId}
              buttonId={button.id}
              errors={errors}
              showErrors={showErrors}
              dirty={dirty}
            />
          </Popover.Body>
          <Popover.Footer className="justify-between">
            {buttonIsNotDraggable ? (
              <div />
            ) : (
              <div
                className="font-semibold text-danger c-pointer d-flex align-center"
                onClick={onRemove}
                data-test-id="button-builder-delete-button"
              >
                <ManyUIIcon.DeleteForever size={24} className="m-r-xxs" />
                <span className="">{l.translate('Remove')}</span>
              </div>
            )}

            {!hideSaveButton && (
              <BtnV2
                variant="primary"
                onClick={onDeselect}
                data-test-id="button-builder-done-button"
                data-onboarding-id="button-builder-done-button"
              >
                {l.translate('Done')}
              </BtnV2>
            )}
          </Popover.Footer>
        </>
      ) : (
        <>
          <Popover.Header>{l.translate('Button Info')}</Popover.Header>
          <ButtonStatsViewer builderId={builderId} button={button} />
        </>
      )}
    </Popover>
  )
}
